import React, { useState, useEffect, useContext, useMemo } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Modal from "bootstrap/js/dist/modal";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import Select, { components } from "react-select";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import styled from "styled-components";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";

const route = all_routes;

const TeamManagementLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading team management data...</span>
  </div>
);

const TableContainer = styled.div`
  max-height: calc(10 * 65px);
  min-height: calc(10 * 65px);
  overflow-y: auto;
`;

const MemberChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  min-height: 20px;
`;

const MemberChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8f9fa;
  border: 1px solid #e3e7ef;
  border-radius: 20px;
  padding: 4px 8px 4px 4px;
  font-size: 14px;
  color: #495057;
`;

const MemberAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const RemoveBtn = styled.button`
  border: none;
  background: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  &:hover {
    background: #dc3545;
    color: white;
  }
`;

interface User {
  tenantUserId: number;
  userId: number;
  name: string;
  email: string;
  role: string;
  role_id?: string | number | null;
  status: "Active" | "Inactive";
  created_date: string;
  avatar_url?: string;
}

const MySwal = withReactContent(Swal);
const fallbackAvatar =
  "https://cloudee-sales-crm-artifact.s3.ap-southeast-7.amazonaws.com/public-artifacts/user/male.png";

const TeamManagement = () => {
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);

  const [apiBatchPage, setApiBatchPage] = useState(1);

  const [apiUserBatchPage, setApiUserBatchPage] = useState(1);

  const [users, setUsers] = useState<User[]>([]);
  const rowsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingNew, setIsSavingNew] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [teams, setTeams] = useState<any[]>([]);
  const [isTeamsLoading, setIsTeamsLoading] = useState(false);
  const [editTeamModalOpen, setEditTeamModalOpen] = useState(false);
  const [editTeam, setEditTeam] = useState<any | null>(null);
  const [editTeamMembers, setEditTeamMembers] = useState<any[]>([]);
  const [isEditTeamLoading, setIsEditTeamLoading] = useState(false);

  const [teamName, setTeamName] = useState("");
  const [teamManager, setTeamManager] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [isTeamActionLoading, setIsTeamActionLoading] = useState(false);

  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [selectAllTeams, setSelectAllTeams] = useState(false);

  const handleSelectAllTeams = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAllTeams(checked);
    if (checked) {
      const validIds = visibleTeams
        .filter((item) => item.team_id)
        .map((item) => item.team_id);
      setSelectedTeamIds(validIds);
    } else {
      setSelectedTeamIds([]);
    }
  };

  const handleSelectTeam = (teamId: number) => {
    setSelectedTeamIds((prev) => {
      const newSelected = prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId];

      const validCurrentData = visibleTeams.filter((item) => item.team_id);
      setSelectAllTeams(
        newSelected.length === validCurrentData.length &&
          validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  const getPageNumbers = () => {
    const maxButtons = 3;
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const currentUserRole = useMemo(() => {
    if (!auth?.tenants || !selectedTenantId) return null;
    const currentTenant = auth.tenants.find(
      (t: any) => String(t.tenant_id) === String(selectedTenantId)
    );
    return currentTenant?.role_key || null;
  }, [auth?.tenants, selectedTenantId]);

  const fetchUsers = async (pageOfFifty: number) => {
    if (!selectedTenantId) return;
    setIsLoading(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(
        (data.results || []).map((item: any) => ({
          tenantUserId: item.tenant_user_id,
          userId: item.user_id,
          name: `${item.user_first_name || ""} ${
            item.user_last_name || ""
          }`.trim(),
          email: item.email || "-",
          role: item.role_display_name || "-",
          status: item.is_active ? "Active" : "Inactive",
          created_date: item.created_datetime?.split("T")[0] || "-",
          avatar_url: item.avatar_url || "",
        }))
      );
    } catch (error) {
      console.error(error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeams = async (pageOfFifty: number) => {
    if (!selectedTenantId) return;
    setIsTeamsLoading(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/team/?tenant_id=${selectedTenantId}&page=${pageOfFifty}&page_size=50`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch teams");
      const data = await res.json();
      setTeams(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / rowsPerPage));
    } catch (error) {
      console.error(error);
      setTeams([]);
      MySwal.fire({
        icon: "error",
        title: "Failed to fetch teams",
        text:
          error instanceof Error
            ? error.message
            : "An error occurred while fetching teams.",
        showConfirmButton: true,
      });
    } finally {
      setIsTeamsLoading(false);
    }
  };

  const inBatchPage = (currentPage - 1) % 5;
  const startInBatch = inBatchPage * rowsPerPage;
  const visibleTeams = teams.slice(startInBatch, startInBatch + rowsPerPage);

  const batchIndex = Math.floor((currentPage - 1) / 5) + 1;
  const userBatchIndex = Math.floor((currentPage - 1) / 5) + 1;

  useEffect(() => {
    setApiBatchPage(batchIndex);
  }, [batchIndex, selectedTenantId]);

  useEffect(() => {
    setApiUserBatchPage(userBatchIndex);
  }, [userBatchIndex, selectedTenantId]);

  useEffect(() => {
    fetchTeams(apiBatchPage);
  }, [selectedTenantId, apiBatchPage]);

  useEffect(() => {
    fetchUsers(apiUserBatchPage);
  }, [selectedTenantId, apiUserBatchPage]);

  useEffect(() => {
    const checkModalStates = () => {
      const hasVisibleModals =
        document.querySelectorAll(".modal.show").length > 0;
      const hasModalBackdrop =
        document.querySelectorAll(".modal-backdrop").length > 0;
      const hasModalOpen = document.body.classList.contains("modal-open");
      const hasOverflowHidden = document.body.style.overflow === "hidden";

      if (
        (hasModalBackdrop || hasModalOpen || hasOverflowHidden) &&
        !hasVisibleModals
      ) {
        document
          .querySelectorAll(".modal-backdrop")
          .forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.documentElement.style.overflow = "";
      }
    };

    checkModalStates();
    const interval = setInterval(checkModalStates, 1000);

    return () => {
      clearInterval(interval);
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const cleanupModal = (modalId: string) => {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
      modalEl.classList.remove("show");
      modalEl.style.display = "none";
      modalEl.setAttribute("aria-hidden", "true");
      modalEl.removeAttribute("aria-modal");
    }

    const resetScroll = () => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
    };

    resetScroll();
    setTimeout(resetScroll, 100);
    setTimeout(resetScroll, 300);
  };

  const fetchTeamMembers = async (teamId: number) => {
    const authHeader = localStorage.getItem("authCred") || "";
    try {
      const res = await fetch(`${API_BASE_URL}/team-user/?team_id=${teamId}`, {
        headers: { Authorization: `Basic ${authHeader}` },
      });
      if (!res.ok) throw new Error("Failed to fetch team members");
      const data = await res.json();
      return data.results || [];
    } catch {
      return [];
    }
  };

  const handleSaveTeam = async () => {
    if (!teamName || !teamManager) {
      return MySwal.fire({
        icon: "error",
        title: "Please fill in all fields",
        showConfirmButton: true,
      });
    }
    setIsSavingNew(true);
    const authHeader = localStorage.getItem("authCred") || "";
    try {
      const res = await fetch(`${API_BASE_URL}/team/`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${authHeader}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: teamName,
          manager_id: teamManager.value,
          tenant_id: selectedTenantId,
          created_by: auth?.user_id,
        }),
      });
      if (!res.ok) throw new Error("Failed to create team");
      const team = await res.json();

      for (const member of teamMembers) {
        await fetch(`${API_BASE_URL}/team-user/`, {
          method: "POST",
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            team_id: team.team_id,
            user_id: member.value,
          }),
        });
      }
      cleanupModal("add_user");
      setTeamName("");
      setTeamManager(null);
      setTeamMembers([]);

      await MySwal.fire({
        icon: "success",
        title: "Team created successfully",
        showConfirmButton: false,
        timer: 1500,
      });

      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";

      setIsTeamActionLoading(true);
      await fetchTeams(apiBatchPage);
      setIsTeamActionLoading(false);
    } catch {
      MySwal.fire({
        icon: "error",
        title: "Failed to create team. Please try again.",
        showConfirmButton: true,
      });
    } finally {
      setIsSavingNew(false);
    }
  };

  const openEditTeamModal = async (team: any) => {
    setEditTeam({
      team_id: team.team_id,
      name: team.name,
      manager: team.manager,
      manager_id: team.manager_id,
    });
    setIsEditTeamLoading(true);
    setEditTeamMembers([]);
    setEditTeamModalOpen(true);

    const members = await fetchTeamMembers(team.team_id);
    setEditTeamMembers(
      members.map((m: any) => {
        const uid = m.user_id || m.user?.user_id || m.user?.id || m.id || m.team_user_id;
        return {
          value: uid,
          label: users.find((u) => u.userId === uid)?.name || uid,
          team_user_id: m.team_user_id,
        };
      })
    );
    setIsEditTeamLoading(false);
  };

  const handleEditTeamSave = async () => {
    if (!editTeam?.name || !editTeam?.manager_id) {
      return MySwal.fire({
        icon: "error",
        title: "Please fill in all fields",
        showConfirmButton: true,
      });
    }
    setIsSavingEdit(true);
    const authHeader = localStorage.getItem("authCred") || "";
    try {
      const res = await fetch(`${API_BASE_URL}/team/${editTeam.team_id}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Basic ${authHeader}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: editTeam.name,
          manager_id: editTeam.manager_id,
          tenant_id: selectedTenantId,
          updated_by: auth?.user_id,
        }),
      });
      if (!res.ok) throw new Error("Failed to update team");

      const currentMembers = await fetchTeamMembers(editTeam.team_id);
      const currentUserIds = currentMembers.map((m: any) => m.user_id);
      const newUserIds = editTeamMembers.map((m: any) => m.value);

      for (const m of currentMembers) {
        if (!newUserIds.includes(m.user_id)) {
          await fetch(`${API_BASE_URL}/team-user/${m.team_user_id}/`, {
            method: "DELETE",
            headers: { Authorization: `Basic ${authHeader}` },
          });
        }
      }

      for (const m of editTeamMembers) {
        if (!currentUserIds.includes(m.value)) {
          await fetch(`${API_BASE_URL}/team-user/`, {
            method: "POST",
            headers: {
              Authorization: `Basic ${authHeader}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              team_id: editTeam.team_id,
              user_id: m.value,
              tenant_id: selectedTenantId,
            }),
          });
        }
      }
      cleanupModal("edit_team");
      setEditTeamModalOpen(false);
      setEditTeam(null);
      setEditTeamMembers([]);

      await MySwal.fire({
        icon: "success",
        title: "Team updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });

      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";

      setIsTeamActionLoading(true);
      await fetchTeams(apiBatchPage);
      setIsTeamActionLoading(false);
    } catch {
      MySwal.fire({
        icon: "error",
        title: "Failed to update team. Please try again.",
        showConfirmButton: true,
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    setIsDeleting(true);
    const authHeader = localStorage.getItem("authCred") || "";
    try {
      const res = await fetch(`${API_BASE_URL}/team/${teamId}/`, {
        method: "DELETE",
        headers: { Authorization: `Basic ${authHeader}` },
      });
      if (!res.ok) throw new Error("Failed to delete team");
      await MySwal.fire({
        icon: "success",
        title: "Team deleted successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      fetchTeams(apiBatchPage);
    } catch {
      MySwal.fire({
        icon: "error",
        title: "Could not delete team. Please try again.",
        showConfirmButton: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const shouldShowTab = (tabName: string) => {
    if (!userRole) return false;

    const roleKey = userRole.role_key;

    switch (tabName) {
      case "general":
        return [
          "CLIENTADMIN",
          "SUPERADMIN",
          "SALESUSER",
          "PLATFORM_ANALYST",
        ].includes(roleKey);
      case "organization":
        return roleKey === "CLIENTADMIN";
      case "lifecycle":
        return [
          "CLIENTADMIN",
          "SUPERADMIN",
          "SALESUSER",
          "PLATFORM_ANALYST",
        ].includes(roleKey);
      case "access":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST"].includes(
          roleKey
        );
      case "subscription":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST"].includes(
          roleKey
        );
      case "tenant":
        return roleKey === "SUPERADMIN" || roleKey === "PLATFORM_ANALYST";
      case "import":
        return roleKey === "CLIENTADMIN";
      case "integration":
        return roleKey === "CLIENTADMIN";
      default:
        return false;
    }
  };

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="access"
                    variant="bottom"
                    includeIcons={true}
                    context="access-subscription"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                {/* Sidebar */}
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link to={route.userManagement} className="fw-medium">
                            User Management
                          </Link>
                          <Link
                            to={route.teamManagement}
                            className="fw-medium active"
                          >
                            Team Management
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-9 col-lg-12">
                  {/* Custom Fields */}
                  <div className="card">
                    <div className="card-body">
                      {/* Search */}
                      <div className="row">
                        <div className="col-xl-3 col-md-3 col-lg-5">
                          <h4 className="fw-semibold mb-3">Teams</h4>
                        </div>
                        <div className="col-xl-9 col-md-9 col-lg-12">
                          <div className="d-flex align-items-center flex-wrap justify-content-sm-end">
                            {currentUserRole !== "PLATFORM_ANALYST" && (
                              <button
                                type="button"
                                className="btn btn-primary mb-3"
                                data-bs-toggle="modal"
                                data-bs-target="#add_user"
                              >
                                <i className="ti ti-square-rounded-plus me-2" />
                                Add Team
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* /Search */}

                      {/* Contact List */}
                      <div className="table-responsive custom-table">
                        {isLoading || isTeamActionLoading ? (
                          <TeamManagementLoader />
                        ) : (
                          <>
                            <TableContainer>
                              <table
                                className="table dataTable no-footer"
                                id="team-list"
                              >
                                <thead className="thead-light">
                                  <tr>
                                    {/* <th style={{ width: 40 }}>
                                      <div className="form-check form-check-md">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={!!selectAllTeams}
                                          onChange={handleSelectAllTeams}
                                          aria-label="Select all teams"
                                        />
                                      </div>
                                    </th> */}
                                    <th>Team Name</th>
                                    <th>Assigned Manager</th>
                                    <th>Team Members</th>
                                    {currentUserRole !== "PLATFORM_ANALYST" && (
                                      <th>Actions</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {isTeamsLoading ? (
                                    <tr>
                                      <td colSpan={5}>Loading...</td>
                                    </tr>
                                  ) : teams.length === 0 ? (
                                    <tr>
                                      <td colSpan={5}>No teams found.</td>
                                    </tr>
                                  ) : (
                                    visibleTeams.map((team) => (
                                      <tr key={team.team_id}>
                                        {/* <td>
                                          <div className="form-check form-check-md">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              checked={
                                                !!selectedTeamIds.includes(
                                                  team.team_id
                                                )
                                              }
                                              onChange={() =>
                                                handleSelectTeam(team.team_id)
                                              }
                                              aria-label={`Select team ${team.name}`}
                                            />
                                          </div>
                                        </td> */}
                                        <td>
                                          <Link
                                            to="#"
                                            data-bs-toggle="modal"
                                            data-bs-target="#edit_team"
                                            onClick={() => openEditTeamModal(team)}
                                            className="fw-medium text-dark"
                                          >
                                            {team.name}
                                          </Link>
                                        </td>
                                        <td>
                                          {team.manager?.full_name || "-"}
                                        </td>
                                        <td>{team.total_team_members ?? 0}</td>
                                        {currentUserRole !==
                                          "PLATFORM_ANALYST" && (
                                          <td>
                                            <div className="dropdown table-action">
                                              <Link
                                                to="#"
                                                className="action-icon"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                data-bs-boundary="viewport"
                                                data-bs-popper-config='{"strategy":"fixed"}'
                                              >
                                                <i className="fa fa-ellipsis-v" />
                                              </Link>
                                              <div className="dropdown-menu dropdown-menu-end">
                                                <Link
                                                  className="dropdown-item"
                                                  to="#"
                                                  onClick={() =>
                                                    openEditTeamModal(team)
                                                  }
                                                  data-bs-toggle="modal"
                                                  data-bs-target="#edit_team"
                                                >
                                                  <i className="ti ti-edit text-blue" />{" "}
                                                  Edit
                                                </Link>
                                                <Link
                                                  className="dropdown-item"
                                                  to="#"
                                                  onClick={() =>
                                                    handleDeleteTeam(
                                                      team.team_id
                                                    )
                                                  }
                                                >
                                                  <i className="ti ti-trash text-danger" />{" "}
                                                  Delete
                                                </Link>
                                              </div>
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </TableContainer>
                          </>
                        )}
                      </div>
                      {!(isLoading || isTeamActionLoading) && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <small>
                            {(() => {
                              const startIdx = (currentPage - 1) * rowsPerPage;
                              return (
                                <>
                                  Showing {startIdx + 1}–
                                  {Math.min(startIdx + rowsPerPage, totalCount)}{" "}
                                  of {totalCount}
                                </>
                              );
                            })()}
                          </small>
                          <nav>
                            <ul className="pagination mb-0">
                              <li
                                className={`page-item ${
                                  currentPage === 1 ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    setCurrentPage((p) => Math.max(1, p - 1))
                                  }
                                >
                                  Prev
                                </button>
                              </li>
                              {getPageNumbers().map((page) => (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ))}
                              <li
                                className={`page-item ${
                                  currentPage === totalPages ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    setCurrentPage((p) =>
                                      Math.min(totalPages, p + 1)
                                    )
                                  }
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* /Custom Fields */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <>
        {/* Add Team Modal */}
        <div className="modal fade" id="add_user">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Team</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {/* Team Name */}
                <div className="mb-3">
                  <label className="form-label">Team Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    required
                  />
                </div>
                {/* Team Manager */}
                <div className="mb-3">
                  <label className="form-label">Team Manager*</label>
                  <Select
                    options={users.map((u) => ({
                      value: u.userId,
                      label: u.name,
                      avatar_url: u.avatar_url,
                    }))}
                    value={teamManager}
                    onChange={(opt) => setTeamManager(opt)}
                    placeholder="Select Manager"
                    isSearchable
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => String(option.value)}
                  />
                </div>
                {/* Team Members */}
                <div className="mb-3">
                  <label className="form-label">Add Members</label>
                  <Select
                    options={users.map((u) => ({
                      value: u.userId,
                      label: u.name,
                      avatar_url: u.avatar_url,
                    }))}
                    value={teamMembers}
                    onChange={(opts) =>
                      setTeamMembers(Array.isArray(opts) ? opts : [])
                    }
                    isMulti
                    placeholder="Select members…"
                    isSearchable
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => String(opt.value)}
                    closeMenuOnSelect={true}
                    controlShouldRenderValue={false}
                  />
                  {/* Show selected members as chips with avatar image or fallback */}
                  <MemberChipContainer>
                    {Array.isArray(teamMembers) &&
                      teamMembers.map((member, idx) => {
                        const userObj = users.find(
                          (u) => u.userId === member.value
                        );
                        const avatarUrl = userObj?.avatar_url || fallbackAvatar;
                        return (
                          <MemberChip key={member.value}>
                            <MemberAvatar>
                              <img
                                src={avatarUrl}
                                alt={member.label}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  background: "#e3e7ef",
                                }}
                                onError={(e) =>
                                  (e.currentTarget.src = fallbackAvatar)
                                }
                              />
                            </MemberAvatar>
                            <span
                              style={{
                                whiteSpace: "nowrap",
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                color: "#222",
                              }}
                            >
                              {member.label}
                            </span>
                            <RemoveBtn
                              type="button"
                              aria-label={`Remove ${member.label}`}
                              tabIndex={0}
                              onClick={() =>
                                setTeamMembers(
                                  teamMembers.filter((m, i) => i !== idx)
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setTeamMembers(
                                    teamMembers.filter((m, i) => i !== idx)
                                  );
                                }
                              }}
                            >
                              <i className="ti ti-x" />
                            </RemoveBtn>
                          </MemberChip>
                        );
                      })}
                  </MemberChipContainer>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveTeam}
                  disabled={isSavingNew}
                >
                  {isSavingNew ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Add Team Modal */}
        {/* Edit Team Modal */}
        <div className="modal fade" id="edit_team">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Team</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setEditTeamModalOpen(false)}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {isEditTeamLoading ? (
                  <div
                    style={{
                      minHeight: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span>Loading...</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Team Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editTeam?.name || ""}
                        onChange={(e) =>
                          setEditTeam((prev: any) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Team Manager *</label>
                      <Select
                        options={users.map((u) => ({
                          value: u.userId,
                          label: u.name,
                          avatar_url: u.avatar_url,
                        }))}
                        value={
                          editTeam && users.length
                            ? users
                                .map((u) => ({
                                  value: u.userId,
                                  label: u.name,
                                  avatar_url: u.avatar_url,
                                }))
                                .find(
                                  (opt) => opt.value === editTeam.manager_id
                                )
                            : null
                        }
                        onChange={(opt) =>
                          setEditTeam((prev: any) => ({
                            ...prev,
                            manager_id: opt ? opt.value : null,
                          }))
                        }
                        placeholder="Select Manager"
                        isSearchable
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => String(option.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Edit Members</label>
                      <Select
                        options={users.map((u) => ({
                          value: u.userId,
                          label: u.name,
                          avatar_url: u.avatar_url,
                        }))}
                        value={editTeamMembers}
                        onChange={(opts) =>
                          setEditTeamMembers(Array.isArray(opts) ? opts : [])
                        }
                        isMulti
                        placeholder="Select members…"
                        isSearchable
                        getOptionLabel={(opt) => opt.label}
                        getOptionValue={(opt) => String(opt.value)}
                        closeMenuOnSelect={true}
                        controlShouldRenderValue={false}
                      />
                      <MemberChipContainer>
                        {Array.isArray(editTeamMembers) &&
                          editTeamMembers.map((member, idx) => {
                            const userObj = users.find(
                              (u) => u.userId === member.value
                            );
                            const avatarUrl =
                              userObj?.avatar_url || fallbackAvatar;
                            return (
                              <MemberChip key={member.value}>
                                <MemberAvatar>
                                  <img
                                    src={avatarUrl}
                                    alt={member.label}
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      background: "#e3e7ef",
                                    }}
                                    onError={(e) =>
                                      (e.currentTarget.src = fallbackAvatar)
                                    }
                                  />
                                </MemberAvatar>
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    color: "#222",
                                  }}
                                >
                                  {member.label}
                                </span>
                                <RemoveBtn
                                  type="button"
                                  aria-label={`Remove ${member.label}`}
                                  tabIndex={0}
                                  onClick={() =>
                                    setEditTeamMembers(
                                      editTeamMembers.filter(
                                        (m, i) => i !== idx
                                      )
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      setEditTeamMembers(
                                        editTeamMembers.filter(
                                          (m, i) => i !== idx
                                        )
                                      );
                                    }
                                  }}
                                >
                                  <i className="ti ti-x" />
                                </RemoveBtn>
                              </MemberChip>
                            );
                          })}
                      </MemberChipContainer>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                  onClick={() => setEditTeamModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleEditTeamSave}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit Team Modal */}


      </>
    </div>
  );
};

export default TeamManagement;
